/**
 * The admin jump-to box (Ctrl/Cmd+K) and the admin keyboard shortcuts.
 *
 * Only active on admin pages (utils/adminDestinations.js#isAdminPath): the
 * studio has its own single-key shortcuts, and navigating away from it ends a
 * live broadcast, so nothing here may run there.
 *
 *   Ctrl/Cmd+K    open / close the jump-to box
 *   g then <key>  go somewhere (GO_SHORTCUTS)
 *   /             put the cursor in the page's search box
 *   ?             show the shortcut list
 *   Esc           close
 *
 * Typing a name or email also searches users (GET /admin/users?search=), and a
 * user found can be opened, or opened straight into Extend trial / Change
 * plan. Nothing here changes a user by itself: every user action opens the
 * console's own dialog, where the admin confirms it.
 *
 * Other pages can open it (a "Jump to…" button) with
 *   window.dispatchEvent(new CustomEvent(OPEN_ADMIN_PALETTE_EVENT))
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import { isAdminUser } from '../../utils/adminAccess';
import { isTypingTarget } from '../../utils/shortcutTarget';
import {
  filterDestinations, GO_SHORTCUTS, isAdminPath, USER_ACTIONS, adminUserPath,
} from '../../utils/adminDestinations';
import './AdminCommandPalette.css';

export const OPEN_ADMIN_PALETTE_EVENT = 'canira:open-admin-palette';
export const CHORD_WINDOW_MS = 1500;
const USER_SEARCH_DELAY_MS = 250;
const USER_RESULTS = 5;
const MAX_PAGE_RESULTS = 8;

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');
export const paletteShortcutLabel = () => (isMac() ? '⌘K' : 'Ctrl K');

const userName = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;

/** Opens the jump-to box from a button on any admin page. */
export function openAdminPalette() {
  window.dispatchEvent(new CustomEvent(OPEN_ADMIN_PALETTE_EVENT));
}

export default function AdminCommandPalette() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const active = isAdminPath(location.pathname) && isAdminUser(currentUser);

  const [open, setOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [users, setUsers] = useState([]);
  const [userState, setUserState] = useState('idle'); // idle | loading | done | error
  const inputRef = useRef(null);
  const chordRef = useRef({ armed: false, timer: null });
  const searchSeqRef = useRef(0);
  const returnFocusRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setShowHelp(false);
    setQuery('');
    setUsers([]);
    setUserState('idle');
    setSelected(0);
    const el = returnFocusRef.current;
    returnFocusRef.current = null;
    if (el && typeof el.focus === 'function') {
      try { el.focus(); } catch (_) { /* gone */ }
    }
  }, []);

  const openPalette = useCallback((help = false) => {
    if (typeof document !== 'undefined') returnFocusRef.current = document.activeElement;
    setShowHelp(help);
    setOpen(true);
  }, []);

  const go = useCallback((path) => {
    close();
    navigate(path);
  }, [close, navigate]);

  // Leaving the admin area closes it.
  useEffect(() => { if (!active && open) close(); }, [active, open, close]);

  // Opened from a button elsewhere.
  useEffect(() => {
    if (!active) return undefined;
    const onOpen = () => openPalette(false);
    window.addEventListener(OPEN_ADMIN_PALETTE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ADMIN_PALETTE_EVENT, onOpen);
  }, [active, openPalette]);

  // Global shortcuts.
  useEffect(() => {
    if (!active) return undefined;
    const chord = chordRef.current;
    const disarm = () => {
      chord.armed = false;
      if (chord.timer) { clearTimeout(chord.timer); chord.timer = null; }
    };
    const onKey = (e) => {
      const key = String(e.key || '');
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && key.toLowerCase() === 'k') {
        e.preventDefault();
        disarm();
        if (open) close(); else openPalette(false);
        return;
      }
      if (open) return; // the box handles its own keys
      if (e.ctrlKey || e.metaKey || e.altKey) { disarm(); return; }
      if (isTypingTarget(e.target)) { disarm(); return; }
      if (chord.armed) {
        const target = GO_SHORTCUTS.find((s) => s.key === key.toLowerCase());
        disarm();
        if (target) { e.preventDefault(); navigate(target.path); }
        return;
      }
      if (key === 'g') {
        chord.armed = true;
        chord.timer = setTimeout(disarm, CHORD_WINDOW_MS);
        return;
      }
      if (key === '?') { e.preventDefault(); openPalette(true); return; }
      if (key === '/') {
        const box = document.querySelector('main input[type="search"], main input[placeholder*="earch"]');
        if (box) { e.preventDefault(); box.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); disarm(); };
  }, [active, open, close, openPalette, navigate]);

  // Focus the box when it opens.
  useEffect(() => {
    if (open && !showHelp && inputRef.current) inputRef.current.focus();
  }, [open, showHelp]);

  // Search users once there's something to search for.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) { setUsers([]); setUserState('idle'); return undefined; }
    const seq = ++searchSeqRef.current;
    setUserState('loading');
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/admin/users', {
          params: { search: q, limit: USER_RESULTS, page: 1 },
          suppressGlobalError: true, // the box says so itself
        });
        if (seq !== searchSeqRef.current) return;
        setUsers(Array.isArray(res.data?.users) ? res.data.users.slice(0, USER_RESULTS) : []);
        setUserState('done');
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        console.error('[AdminPalette] user search failed', err);
        setUsers([]);
        setUserState('error');
      }
    }, USER_SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, query]);

  const pages = useMemo(() => filterDestinations(query).slice(0, MAX_PAGE_RESULTS), [query]);

  // One list for the arrow keys: pages first, then users.
  const items = useMemo(() => ([
    ...pages.map((d) => ({ type: 'page', id: d.key, path: d.path })),
    ...users.map((u) => ({ type: 'user', id: `user:${u.id}`, path: adminUserPath(u.id) })),
  ]), [pages, users]);

  useEffect(() => { setSelected(0); }, [query]);
  useEffect(() => {
    if (selected >= items.length) setSelected(Math.max(0, items.length - 1));
  }, [items.length, selected]);

  const dialogRef = useRef(null);
  const onBoxKey = (e) => {
    // Escape closes the box only; the page underneath (an open menu) keeps its state.
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    // Keep Tab inside the box while it is open.
    if (e.key === 'Tab' && dialogRef.current) {
      const focusables = Array.from(dialogRef.current.querySelectorAll('button, input, [href], [tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.disabled);
      if (focusables.length) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); return; }
        if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); return; }
      }
    }
    if (showHelp) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => (items.length ? (i + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      if (e.target !== inputRef.current) return; // a focused button handles its own Enter
      const item = items[selected];
      if (item) { e.preventDefault(); go(item.path); }
    }
  };

  if (!active || !open || typeof document === 'undefined') return null;

  const userOffset = pages.length;
  const q = query.trim();

  return createPortal(
    <div className="acp-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div
        ref={dialogRef}
        className="acp-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={showHelp ? 'Keyboard shortcuts' : 'Jump to'}
        onKeyDown={onBoxKey}
      >
        {showHelp ? (
          <div className="acp-help">
            <div className="acp-help-head">
              <h2>Keyboard shortcuts</h2>
              <button type="button" className="acp-link" onClick={() => setShowHelp(false)} autoFocus>
                Back to search
              </button>
            </div>
            <dl>
              <div><dt><kbd>{paletteShortcutLabel()}</kbd></dt><dd>Jump to a page or a user</dd></div>
              {GO_SHORTCUTS.map((s) => (
                <div key={s.key}><dt><kbd>g</kbd> then <kbd>{s.key}</kbd></dt><dd>{s.label}</dd></div>
              ))}
              <div><dt><kbd>/</kbd></dt><dd>Search on this page</dd></div>
              <div><dt><kbd>?</kbd></dt><dd>This list</dd></div>
              <div><dt><kbd>Esc</kbd></dt><dd>Close</dd></div>
            </dl>
            <p className="acp-note">Shortcuts work on admin pages only, and not while you're typing in a box.</p>
          </div>
        ) : (
          <>
            <div className="acp-input-row">
              <input
                ref={inputRef}
                className="acp-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Jump to a page, or find a user by name or email…"
                aria-label="Jump to a page or user"
                role="combobox"
                aria-expanded="true"
                aria-controls="acp-pages"
                aria-activedescendant={selected < pages.length ? `acp-opt-${selected}` : undefined}
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="acp-esc">Esc</kbd>
            </div>
            <div className="acp-results">
              {pages.length > 0 && <div className="acp-group" id="acp-pages-label">Pages</div>}
              <div id="acp-pages" role="listbox" aria-labelledby="acp-pages-label">
              {pages.map((d, i) => (
                <button
                  key={d.key}
                  id={`acp-opt-${i}`}
                  tabIndex={-1}
                  type="button"
                  role="option"
                  aria-selected={selected === i}
                  className={`acp-item${selected === i ? ' is-selected' : ''}`}
                  onMouseEnter={() => setSelected(i)}
                  onClick={() => go(d.path)}
                >
                  <span className="acp-item-label">{d.label}</span>
                  <span className="acp-item-hint">{d.hint}</span>
                </button>
              ))}
              </div>

              {q.length >= 2 && <div className="acp-group">Users</div>}
              {q.length >= 2 && userState === 'loading' && users.length === 0 && (
                <div className="acp-empty">Searching users…</div>
              )}
              {q.length >= 2 && userState === 'error' && (
                <div className="acp-empty">Couldn't search users right now. Try again in a moment.</div>
              )}
              {q.length >= 2 && userState === 'done' && users.length === 0 && (
                <div className="acp-empty">No users match "{q}".</div>
              )}
              {users.map((u, j) => {
                const i = userOffset + j;
                return (
                  <div
                    key={u.id}
                    role="group"
                    aria-label={userName(u)}
                    className={`acp-item acp-user${selected === i ? ' is-selected' : ''}`}
                    onMouseEnter={() => setSelected(i)}
                  >
                    <button type="button" className="acp-user-main" onClick={() => go(adminUserPath(u.id))}>
                      <span className="acp-item-label">{userName(u)}</span>
                      <span className="acp-user-email">{u.email}</span>
                    </button>
                    <span className="acp-user-actions">
                      {USER_ACTIONS.filter((a) => a.id !== 'view').map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className="acp-chip"
                          onClick={() => go(adminUserPath(u.id, a.id))}
                        >
                          {a.label}
                        </button>
                      ))}
                    </span>
                  </div>
                );
              })}

              {pages.length === 0 && q.length < 2 && (
                <div className="acp-empty">Nothing matches "{q}".</div>
              )}
            </div>
            <div className="acp-footer">
              <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
              <span><kbd>Enter</kbd> open</span>
              <button type="button" className="acp-link" onClick={() => setShowHelp(true)}>All shortcuts <kbd>?</kbd></button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
